/**
 * Server state for the catalog pages: stores, products, and what correction memory has learned.
 *
 * The settings pattern (see `features/settings/queries.ts`): reads are `queryOptions`, writes are
 * mutations that invalidate whatever their change could have altered. A merge reaches further than
 * it looks: merging stores changes which store a receipt names, so receipts go stale too, and
 * merging products changes what a remembered line points at.
 */

import { queryOptions, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { unwrap, unwrapEmpty } from "@/api/problem";
import type { components } from "@/api/schema";

export type Store = components["schemas"]["StoreOut"];
export type Product = components["schemas"]["ProductOut"];
export type StoreProduct = components["schemas"]["StoreProductOut"];

// --- reads --------------------------------------------------------------------------------------

export const storesQuery = queryOptions({
  queryKey: ["stores"],
  queryFn: async () => unwrap(await api.GET("/api/v1/stores")),
});

export const productsQuery = queryOptions({
  queryKey: ["products"],
  queryFn: async () => unwrap(await api.GET("/api/v1/products")),
});

/** What one store has taught correction memory, newest first. */
export function rememberedQuery(storeId: string) {
  return queryOptions({
    queryKey: ["store-products", storeId],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/stores/{store_id}/products", {
          params: { path: { store_id: storeId } },
        }),
      ),
  });
}

/** What a row can be merged into: every other row, by name. */
export function mergeTargets<T extends { id: string; name: string }>(rows: T[], id: string): T[] {
  return rows.filter((row) => row.id !== id).sort((a, b) => a.name.localeCompare(b.name));
}

// --- writes -------------------------------------------------------------------------------------

async function invalidate(queryClient: QueryClient, ...keys: string[]) {
  await Promise.all(keys.map((key) => queryClient.invalidateQueries({ queryKey: [key] })));
}

export function useRenameStore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) =>
      unwrap(
        await api.PATCH("/api/v1/stores/{store_id}", {
          params: { path: { store_id: id } },
          body: { name },
        }),
      ),
    // Receipts show their store's name.
    onSuccess: () => invalidate(queryClient, "stores", "receipts", "receipt"),
  });
}

export function useMergeStores() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, intoId }: { id: string; intoId: string }) =>
      unwrap(
        await api.POST("/api/v1/stores/{store_id}/merge", {
          params: { path: { store_id: id } },
          body: { into_id: intoId },
        }),
      ),
    onSuccess: () => invalidate(queryClient, "stores", "receipts", "receipt", "store-products"),
  });
}

export function useForget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrapEmpty(
        await api.DELETE("/api/v1/store-products/{mapping_id}", {
          params: { path: { mapping_id: id } },
        }),
      ),
    onSuccess: () => invalidate(queryClient, "store-products"),
  });
}

export function useRenameProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) =>
      unwrap(
        await api.PATCH("/api/v1/products/{product_id}", {
          params: { path: { product_id: id } },
          body: { name },
        }),
      ),
    // A remembered line shows the name of the product it points at.
    onSuccess: () => invalidate(queryClient, "products", "store-products"),
  });
}

export function useMergeProducts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, intoId }: { id: string; intoId: string }) =>
      unwrap(
        await api.POST("/api/v1/products/{product_id}/merge", {
          params: { path: { product_id: id } },
          body: { into_id: intoId },
        }),
      ),
    onSuccess: () => invalidate(queryClient, "products", "store-products", "receipts", "receipt"),
  });
}
